import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { FavoriteHeartButton } from '@/components/favorites/favorite-heart-button'
import { HandleChip } from '@/components/users/handle-chip'
import { ProjectHeroBackground } from '@/components/projects/project-hero-background'
import { AISeedBadge } from '@/components/community/ai-seed-badge'
import { CommunityChip } from '@/components/community/community-chip'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { BrowseProject } from '@/api/types'

interface BrowseProjectCardProps {
  project: BrowseProject
  /** Phase 10: drives heart visibility — anonymous viewers don't get a heart on browse cards. */
  isAuthenticated: boolean
  /** Phase 10: filled-state of the heart, fetched via the bulk enrichment query upstream. */
  isFavoritedByMe: boolean
}

export const BrowseProjectCard = ({
  project,
  isAuthenticated,
  isFavoritedByMe,
}: BrowseProjectCardProps) => (
  <Card className="relative overflow-hidden">
    {isAuthenticated && (
      <div className="absolute right-2 top-2 z-10">
        <FavoriteHeartButton
          projectId={project.id}
          isFavoritedByMe={isFavoritedByMe}
          variant="overlay"
        />
      </div>
    )}
    <ProjectHeroBackground thumbUrl={project.heroThumbUrl} fallbackSeed={project.id}>
      <Link
        to={canonicalProjectUrl(project.author.handle, project.slug)}
        className={
          'block text-base font-semibold leading-snug hover:underline ' +
          (project.heroThumbUrl ? 'drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]' : '')
        }
      >
        {project.title}
      </Link>
      <div
        className={
          'flex flex-wrap items-center gap-x-2 gap-y-1 text-xs ' +
          (project.heroThumbUrl
            ? 'text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]'
            : 'text-muted-foreground')
        }
      >
        <HandleChip author={project.author} className="text-xs" />
        {project.author.badgeSeedStatus && <AISeedBadge />}
        <span aria-hidden>·</span>
        <span>Updated {new Date(project.updatedAt).toLocaleDateString()}</span>
        {project.community && (
          <>
            <span aria-hidden>·</span>
            <CommunityChip community={project.community} onClick={(e) => e.stopPropagation()} />
          </>
        )}
      </div>
    </ProjectHeroBackground>
    {project.descriptionExcerpt && (
      <CardContent className="p-4 pt-3">
        <p className="text-sm text-muted-foreground">{project.descriptionExcerpt}</p>
      </CardContent>
    )}
  </Card>
)
